/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEqual, get, max } from 'lodash';
import { Grid, Col, Row } from 'react-bootstrap';
import numeral from 'numeral';

import ComponentHeader from '../../components/Headers/component-header';
import Iframe from '../../components/Iframe';
import InlineQuery from '../../components/InlineQuery';
import MobilityScores from '../../components/MobilityScores';
import Table from '../../components/Table';

import formatMobilityScores from '../../utils/network';

import PageHeader from '../../components/PageHeader';
import TitleBanner from '../../components/TitleBanner';
import { TabbedFilters, FilterTypes } from '../../components/Filters';
import { extractFilterValues } from '../../utils/filters';

import {
  topShowsColumns,
  trendingColumns,
  trendingDefaultSorts,
  topShowsDefaultSorts,
  anticipatedDefaultSorts,
  anticipatedColumns,
  formatGenderDemo
} from './table-config';

import {
  CountryDemo,
  AgeDemo,
  GenderDemo
} from '../../components/Charts/DemoCharts';
import { downloadReportFunc } from '../../utils/export';
import ModuleExport from '../../components/ModuleExport';

import networksConfig from '../configs/networks-config';

import './NetworkReport.css';

const NetworkReport = ({
  network,
  filters,
  queries,
  queryActions: { runFormattedInlineQueriesForPage },
  filtersActions: { getFiltersForPageAsync, resetFilters },
  fetchNetwork,
  match: {
    params: { networkId }
  },
  blocked
}) => {
  const didMountRef = useRef(false);
  const [selectedFilters, setSelectedFilters] = useState({});

  const updateQueries = () => {
    const filterOverrides = {};

    filterOverrides.released = {
      id: 'airingStatus',
      value: { value: '-Upcoming' }
    };

    runFormattedInlineQueriesForPage(networksConfig.NETWORK_REPORT_FILTERS, {
      ...filters.fields,
      ...filterOverrides
    });
  };

  useEffect(() => {
    if (network) {
      document.title = `${network.name} | Network | CVM Insights`;
    } else {
      document.title = 'Loading | CVM Insights';
    }

    return () => {
      document.title = document.URL;
    };
  }, [network]);

  useEffect(() => {
    fetchNetwork(networkId);
    // NOTE: workaround - need to reload filters as they are being cleared out on TabbedFilters component unmount
    getFiltersForPageAsync(networksConfig.NETWORK_PAGE_ID);
  }, [networkId]);

  useEffect(() => {
    if (blocked) {
      return;
    }

    const values = extractFilterValues(filters.fields);
    const hasNewValues = !isEqual(values, selectedFilters);
    if (didMountRef.current && hasNewValues && filters.groupings.length) {
      setSelectedFilters(values);
      updateQueries();
    }
  }, [blocked, filters.fields]);

  useEffect(() => {
    didMountRef.current = true;
    return resetFilters;
  }, []);

  const renderNetworkContent = () => {
    const mobilityTitle = `${network.name} Viewers' Preferred Viewing Device`;
    const networkMostAnticipatedShowsTitle = `Most Anticipated ${network.name} Shows`;
    const networkTopPerformingShowsTitle = `Top Performing ${network.name} Shows`;
    const networkTrendingShowsTitle = `${network.name} Shows on the Rise`;
    const {
      networkMobility,
      networkTopShows,
      anticipatedUnairedShows,
      networkTrendingShows,
      networkGenderIndex,
      networkAgeIndex,
      networkCountryIndex
    } = queries;

    return (
      <>
        <TabbedFilters defaultState />
        <section className="tvt-main__content network-report-page">
          <Grid fluid componentClass="section">
            <Row>
              <Col xs={12} md={6}>
                <ComponentHeader title={networkTopPerformingShowsTitle} />
                <InlineQuery useChildForLoading query={networkTopShows}>
                  {query => (
                    <Table
                      data={query.data}
                      headerFormatter={topShowsColumns}
                      defaultSorts={topShowsDefaultSorts}
                      view="actorTopShows"
                      filters={filters.fields}
                      showRank={false}
                      height={218}
                      queryMeta={
                        networksConfig.NETWORK_REPORT_FILTERS.queries
                          .networkTopShows
                      }
                    />
                  )}
                </InlineQuery>
                <div className="network-report__anticipatedshows-wrapper">
                  <ComponentHeader title={networkMostAnticipatedShowsTitle} />
                  <InlineQuery
                    useChildForLoading
                    query={anticipatedUnairedShows}
                  >
                    {query => (
                      <Table
                        data={query.data}
                        headerFormatter={anticipatedColumns}
                        defaultSorts={anticipatedDefaultSorts}
                        view="anticipatedUnairedShows"
                        filters={filters.fields}
                        showRank={false}
                        height={170}
                        queryMeta={
                          networksConfig.NETWORK_REPORT_FILTERS.queries
                            .anticipatedUnairedShows
                        }
                      />
                    )}
                  </InlineQuery>
                </div>
                <div className="actor_report__demo-chart-wrapper">
                  <ComponentHeader
                    title={
                      <span>
                        Country Index of <em>{get(network, 'name', '')}</em>
                        {' Viewers'}
                      </span>
                    }
                  />
                  <ModuleExport
                    fetchData
                    tableDataDownload={downloadReportFunc(
                      'networkCountryIndex',
                      {
                        ...filters.fields,
                      },
                      networksConfig.NETWORK_REPORT_FILTERS.queries
                        .networkCountryIndex,
                      'index'
                    )}
                    csv
                    xls
                    png
                  />
                  <div className="tvt-actor-report__countryDemo-chart-wrapper">
                    <CountryDemo
                      maxValue={max(
                        networkCountryIndex.data.map(c => Math.abs(c.index))
                      )}
                      query={networkCountryIndex}
                      keys={['index']}
                      indexKey="country"
                      valueKey="index"
                    />
                  </div>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <ComponentHeader title={mobilityTitle} />
                <InlineQuery query={networkMobility}>
                  {query => (
                    <MobilityScores list={formatMobilityScores(query)} />
                  )}
                </InlineQuery>
                <div className="network-report__trendingshows-wrapper">
                  <ComponentHeader title={networkTrendingShowsTitle} />
                  <InlineQuery useChildForLoading query={networkTrendingShows}>
                    {query => (
                      <Table
                        data={query.data}
                        headerFormatter={trendingColumns}
                        defaultSorts={trendingDefaultSorts}
                        view="networkTrendingShows"
                        filters={filters.fields}
                        showRank={false}
                        height={280}
                        queryMeta={
                          networksConfig.NETWORK_REPORT_FILTERS.queries
                            .networkTrendingShows
                        }
                      />
                    )}
                  </InlineQuery>
                </div>
                <div className="actor_report__demo-chart-wrapper">
                  <ComponentHeader
                    title={
                      <span>
                        <em>{network.name}</em> Viewer's Age
                      </span>
                    }
                    tooltip="This metric represents a normalized view of
                    this defined audience. It is calculated
                    relative to the overall app population to
                    adjust for app population biases. The
                    percentages represented do not reflect raw
                    viewer demographics."
                  />
                  <ModuleExport
                    fetchData
                    tableDataDownload={downloadReportFunc(
                      'networkAgeIndex',
                      {
                        ...filters.fields,
                      },
                      networksConfig.NETWORK_REPORT_FILTERS.queries
                        .networkAgeIndex,
                      'age'
                    )}
                    csv
                    xls
                    png
                  />
                  <div className="tvt-actor-report__ageDemo-chart-wrapper">
                    <AgeDemo
                      query={networkAgeIndex}
                      layout="vertical"
                      sortKey="age"
                      indexKey="age"
                      width={570}
                      keys={['index']}
                      tooltipFormatter={({ value }) =>
                        `${numeral(value * 100).format('0.0')}%`
                      }
                    />
                  </div>
                </div>
                <div className="actor_report__demo-chart-wrapper">
                  <ComponentHeader
                    title={
                      <span>
                        <em>{network.name}</em> Viewer's Gender
                      </span>
                    }
                    tooltip="This metric represents a normalized view of
                    this defined audience. It is calculated
                    relative to the overall app population to
                    adjust for app population biases. The
                    percentages represented do not reflect raw
                    viewer demographics."
                  />
                  <ModuleExport
                    fetchData
                    tableDataDownload={downloadReportFunc(
                      'networkGenderIndex',
                      {
                        ...filters.fields,
                      },
                      networksConfig.NETWORK_REPORT_FILTERS.queries
                        .networkGenderIndex
                    )}
                    csv
                    xls
                    png
                  />
                  <div className="tvt-actor-report__genderDemo-chart-wrapper">
                    <GenderDemo
                      id="actor_vote_gender_index.user_gender"
                      label="gender"
                      value="index"
                      layout="vertical"
                      width={570}
                      height={288}
                      query={{
                        loading: networkGenderIndex.loading,
                        data: formatGenderDemo(networkGenderIndex)
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Grid>
        </section>
      </>
    );
  };

  if (!network) {
    return null;
  }

  return (
    <>
      <TitleBanner>
        <PageHeader
          title={network.name}
          tooltip="Network metrics are aggregated based on the Originating Network of a Show."
        />
      </TitleBanner>

      {blocked && (
        <Iframe
          blocked
          blockedMessage="Contact your Client Success Representative to learn more about this report"
          url=""
          height="100%"
        />
      )}

      {!blocked && renderNetworkContent()}
    </>
  );
};

NetworkReport.propTypes = {
  blocked: PropTypes.bool.isRequired,

  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,

  match: PropTypes.shape({
    params: PropTypes.shape({ networkId: PropTypes.string })
  }).isRequired,

  fetchNetwork: PropTypes.func.isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPage: PropTypes.func.isRequired,
    getFiltersForPageAsync: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired
  }).isRequired,

  queries: PropTypes.shape({
    networkMobility: PropTypes.shape({}),
    anticipatedUnairedShows: PropTypes.shape({}),
    networkTopShows: PropTypes.shape({}),
    networkTrendingShows: PropTypes.shape({})
  }).isRequired,

  network: PropTypes.shape({
    id: PropTypes.number,
    objectId: PropTypes.string,
    name: PropTypes.string
  })
};

NetworkReport.defaultProps = {
  network: null
};

export default NetworkReport;
